import React, { useState, useEffect, useRef } from 'react';
import {uploadImage} from '../../firebaseActions.mjs';
import {genUUID} from './Misc';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import ReactCrop, {
    centerCrop,
    makeAspectCrop,
    Crop,
    PixelCrop,
    convertToPixelCrop,
} from 'react-image-crop';
import CropperModal from './CropperModal';
import 'react-circular-progressbar/dist/styles.css';
import 'react-image-crop/src/ReactCrop.scss'

const getImageDimensions = ( url ) => {
    return new Promise( (resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => {
            // Natural size is the actual image size regardless of rendering.
            // The 'normal' `width`/`height` are for the **rendered** size.
            const width  = img.naturalWidth;
            const height = img.naturalHeight; 
    
            resolve({width, height});
        };
    
        img.onerror = reject;
    })
}

const autoCrop = (bitmap, width, height) => {
    
    const offscreen = new OffscreenCanvas(
        width,
        height,
    )
    const ctx = offscreen.getContext('2d')
    if (!ctx) return false
    
    ctx.drawImage(
        bitmap,
        0,
        0,
        width,
        height
    )
    
    const imageData = ctx.getImageData(0, 0, width, height).data;
    
    let minX = width, minY = height, maxX = 0, maxY = 0;
    let found = false;
    
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const index = (y * width + x) * 4;
            const alpha = imageData[index + 3];
            if (alpha > 0) {
                found = true;
                minX = Math.min(minX, x);
                minY = Math.min(minY, y);
                maxX = Math.max(maxX, x);
                maxY = Math.max(maxY, y);
            }
        }
    }
    // console.log("minX, minY, maxX - minX + 1, maxY - minY + 1,", 
    //     minX, 
    //     minY, 
    //     maxX - minX + 1, 
    //     maxY - minY + 1,); return;

    
    const outCanvas = new OffscreenCanvas(
        maxX - minX + 1,
        maxY - minY + 1,
    )
    const outCtx = outCanvas.getContext('2d')
    if (!outCtx) return false

    outCtx.drawImage(
        offscreen,
        minX, 
        minY, 
        maxX - minX + 1, 
        maxY - minY + 1,
        0,
        0,
        maxX - minX + 1,
        maxY - minY + 1,
    )

    // outCtx.drawImage(
    //     offscreen,
    //     10, 10, 200, 200
    // )

    return outCanvas

}

function ImageUpload(props) {
    const {file, campaign_id, item, updateAction, extra, dimensions, minWidth, minHeight, maxWidth, maxHeight, aspectRatio, hasCrop, hasAutoCrop} = props;
	const [preview, setPreview] = useState(file);
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(null);
    const [uploadPercentage, setUploadPercentage] = useState(0);
    const [isCropping, setIsCropping] = useState(false)
    const [updateKey, setUpdateKey] = useState(1)
    const [isDimensionError, setIsDimensionError] = useState(false)
    const dimErrTimeout = useRef(null);
    const [suffix, setSuffix] = useState()
    const [fileData, setFileData] = useState()
    const dataURL = useRef()
	const onInputChange = async e => {
		const loadedFile = e.target.files[0]
        const currentSuffix = loadedFile.type.split('/')[1]
        setSuffix(currentSuffix)
		dataURL.current = URL.createObjectURL(loadedFile)
        setUpdateKey( key => key + 1)
        const {width, height} = await getImageDimensions(dataURL.current)
        
        if(hasCrop){
            console.log("dataURL.current", dataURL.current)
            setFileData({
                suffix: currentSuffix,
                dataURL: dataURL.current,
                width: width,
                height: height,
                aspectRatio: aspectRatio
            })
            setIsCropping(true)
            //Note: Cropper modal limits dimensions to 2200
        }
        else{
            const {width, height} = await getImageDimensions(dataURL.current)
            if(width > 2200 || height > 2200 ){
                setIsDimensionError(true)
                if(dimErrTimeout.current){
                    clearTimeout(dimErrTimeout.current)
                }
                dimErrTimeout.current = setTimeout( () => setIsDimensionError(false), 4000)
                return
            }
            if(dimErrTimeout.current){
                clearTimeout(dimErrTimeout.current)
            }
            setIsDimensionError(false)

            if(hasAutoCrop){
                const bitmap = await createImageBitmap(loadedFile);
                const outCanvas = autoCrop(bitmap, width, height)

                let outSettings = {
                    type: 'image/png'
                }
                if (currentSuffix === 'jpg' || currentSuffix === 'jpeg') {
                    outSettings = {
                        type: 'image/jpeg',
                        quality: 0.9
                    }
                }
                const blob = await outCanvas.convertToBlob(outSettings)
            
                const autoCroppedImageFile = new File(
                    [blob],
                    "croppedImage." + currentSuffix,
                    {
                        type: outSettings.type,
                        lastModified: new Date()
                    }
                );
                try {
                    if(dataURL.current) URL.revokeObjectURL(dataURL.current)
                } catch (error) {
                    console.log(error)
                }
                dataURL.current = URL.createObjectURL(autoCroppedImageFile)
                setPreview(dataURL.current)
                startUploadImage(autoCroppedImageFile)
            }
            else{
                setPreview(dataURL.current)
                startUploadImage(loadedFile)
            }
        }
    }
    const startUploadImage = (imageFile) => {
        try {
            if(dataURL.current) URL.revokeObjectURL(dataURL.current)
        } catch (error) {
            console.log(error)
        }
        
        setUploading('uploading');

        uploadImage(
            campaign_id,
            genUUID()+'.'+suffix,
            imageFile,
            (state) => {
                setUploadPercentage(Math.round(100*state.bytesTransferred/state.totalBytes).toFixed(0))
            },
            (fullPath) => {
                setUploading('uploaded');
                setTimeout(() => { setUploading(null) }, 1500)
                updateAction(item, fullPath, extra)
                if(inputRef.current){
                    inputRef.current.value = null;
                }
            },
            (error) => {
                console.log(error);
                setPreview(null);
                if(inputRef.current){
                    inputRef.current.value = null;
                }
                setUploading('error');
                setTimeout(() => { setUploading(null) }, 1500)
            },
        )
    }
    const clear = (e) => {
        e.stopPropagation();
        setPreview(null);
        updateAction(item, null, extra)
    }
    const submitCrop = (croppedImageFile) => {
        try {
            if(dataURL.current) URL.revokeObjectURL(dataURL.current)
        } catch (error) {
            console.log(error)
        }
        setIsCropping(false)
        startUploadImage(croppedImageFile)
    }
    const stopCropping = () => {
		try {
            if(dataURL.current) URL.revokeObjectURL(dataURL.current)
        } catch (error) {
            console.log(error)
        }
        if(inputRef.current){
            inputRef.current.value = null;
        }
        setIsCropping(false)
    }
    useEffect(() => {
        setPreview(props.file);
	}, [props])
	return (
		<>
			<div className="image__upload-placeholder">
                { !isCropping &&
                    <input type="file" key={updateKey} ref={inputRef} onChange={ e => onInputChange(e)} accept="image/jpg, image/png, image/jpeg" />
                }
                { preview &&
                    <>
                        <div className="animate__animated animate__fadeIn image__upload-img">
                            { !uploading &&
                                <>
                                    { preview &&
                                        <img className="img__responsive" src={preview} alt="Preview" />
                                    }
                                    { !preview &&
                                        <div>
                                            <span className="material-icons">
                                                image
                                            </span>
                                            <div className="mtop--10 text--muted font--13">Click to change</div>
                                        </div>
                                    }
                                </>
                            }
                            { uploading === 'uploading' &&
                                <div style={{ width: 50, height: 50 }}>
                                    <CircularProgressbar styles={buildStyles({
                                        pathColor: `rgba(100,92,170, ${uploadPercentage / 100})`,
                                        textColor: '#645CAA',
                                        trailColor: '#645CAA',
                                    })} value={uploadPercentage} text={`${uploadPercentage}%`} />
                                </div>
                            }
                            { uploading === 'uploaded' &&
                                <div className="text--center">
                                    <span className="material-icons font--1em text--success">
                                        task_alt
                                    </span>
                                    <h4>Uploaded</h4>
                                </div>
                            }
                            { uploading === 'error' &&
                                <div className="text--center">
                                    <span className="material-icons font--1em text--danger">
                                        report_problem
                                    </span>
                                    <h4>Upload Error</h4>
                                    <div className="font--12">Please try again later.</div>
                                </div>
                            }
                            { uploading === 'maxFileSizeError' &&
                                <div className="text--center">
                                    <span className="material-icons font--1em text--danger">
                                        report_problem
                                    </span>
                                    <h4>Max file size exceeded</h4>
                                    <div className="font--12">Please try again with a smaller file.</div>
                                </div>
                            }
                            { (uploading === 'imageDimensionError' || isDimensionError) &&
                                <div className="text--center">
                                    <span className="material-icons font--1em text--danger">
                                        report_problem
                                    </span>
                                    <h4>Oversized Image!</h4>
                                    <div className="font--12 text--danger"> Max Width or Height: 2200 </div>
                                </div>
                            }
                        </div>
                        {(!uploading && (preview !== '/images/common/placeholder.png')) && 
                            <span className="image__delete material-icons" onClick={(e) => {
                                e.stopPropagation();
                                clear(e)
                            }}>
                                clear
                            </span>
                        }
                        
                        
                    </>
                }
                { !preview && !isCropping &&
                    <div>
                        <i className="fi fi-rr-upload font--25"></i><br />
                        <div className="font--12">Upload</div>
                        { isDimensionError &&
                            <div className="text--center">
                                <span className="material-icons font--1em text--danger">
                                    report_problem
                                </span>
                                <h4>Invalid image dimensions</h4>
                                <div className="font--12">Please try again with proper dimensions.</div>
                            </div>
                        }
                        <div className="mtop--10 text--muted font--13">.jpg, .png files allowed</div>
                    </div>
                }
                { !isCropping &&
                    <div className="image__upload-info">
                        {dimensions && dimensions+' • '}
                        Max-size: 5Mb
                    </div>
                }
            </div>
            {isCropping && <CropperModal closeCb={stopCropping} confirmCb={submitCrop} fileData={fileData} />}
		</>
	)
}

export default ImageUpload